import { axiosInstance } from "api/axios.instance"
import type {
	TCreateQuotationRequest,
	TGetComponentOptionsParams,
	TUpdateQuotationConfigurationRequest,
} from "types/quotations.type"
import type {
	TConnectFurnitureRequest,
	TElevationNumber,
	TUpdateErefRequest,
	TUpdateErefResponse,
	TUpdateErefStatusRequest,
	TUpdateErefCommentRequest,
	TUpdateEcallRequest,
	TUpdateEcallResponse,
	TCreateEcallElevationRequest,
	TUpdateEcallElevationsRequest,
	TUpdateEcallElevationsResponse,
	TAutoConnectEcallElevationRequest,
	TAutoConnectEcallElevationResponse,
	TAutoConnectErefEcallsRequest,
	TAutoConnectErefEcallsResponse,
	TUpdateFurnitureCategoryRequest,
	TUpdateFurnitureFixedPriceRequest,
	TUpdateFurnitureRequest,
	TUpdateFurnitureConfigRequest,
	TUpdateFurnitureCommentRequest,
	TUpdateFurnitureStatusRequest,
	TUpdateErefConfigurationRequest,
	TDuplicateFurnitureRequest,
	TDuplicateFurnitureResponse,
} from "types/elevationReferences.type"

class QuotationsService {
	async getQuotations(drawingId: number) {
		return axiosInstance.get(`drawings/${drawingId}/quotations/`)
	}

	async getQuotation(quotationId: number) {
		return axiosInstance.get(`quotations/${quotationId}/`)
	}

	async createQuotation(body: TCreateQuotationRequest) {
		return axiosInstance.post(`quotations/`, body)
	}

	async updateQuotationConfiguration(quotationId: number, body: TUpdateQuotationConfigurationRequest) {
		return axiosInstance.patch(`quotations/${quotationId}/`, body)
	}

	async getComponentOptions({ series_id, components, quotation_id }: TGetComponentOptionsParams) {
		return axiosInstance.get(`quotations/component-options/`, {
			params: {
				series_id,
				components: components.join(","),
				...(quotation_id ? { quotation_id } : {}),
			},
		})
	}

	async renameQuotation(quotationId: number, name: string) {
		return axiosInstance.patch(`quotations/${quotationId}/name/`, { name })
	}

	async connectFurnitureToEref(quotationId: number, erefId: number, body: TConnectFurnitureRequest) {
		return axiosInstance.post(`quotations/${quotationId}/furniture/eref/${erefId}/connect/`, body)
	}

	async disconnectFurnitureFromEref(quotationId: number, erefId: number, body: TConnectFurnitureRequest) {
		return axiosInstance.post(`quotations/${quotationId}/furniture/eref/${erefId}/disconnect/`, body)
	}

	async updateEref(quotationId: number, erefId: number, body: TUpdateErefRequest) {
		return axiosInstance.patch<TUpdateErefResponse>(`quotations/${quotationId}/eref/${erefId}/`, body)
	}

	async updateErefStatus(quotationId: number, erefId: number, body: TUpdateErefStatusRequest) {
		return axiosInstance.patch(`quotations/${quotationId}/eref/${erefId}/status/`, body)
	}

	async updateErefComment(quotationId: number, erefId: number, body: TUpdateErefCommentRequest) {
		return axiosInstance.put(`quotations/${quotationId}/eref/${erefId}/comment/`, body)
	}

	async deleteErefComment(quotationId: number, erefId: number, version: number) {
		return axiosInstance.delete(`quotations/${quotationId}/eref/${erefId}/comment/`, {
			params: { version },
		})
	}

	async updateEcall(quotationId: number, ecallId: number, body: TUpdateEcallRequest) {
		return axiosInstance.patch<TUpdateEcallResponse>(`quotations/${quotationId}/ecall/${ecallId}/`, body)
	}

	async createEcallElevation(quotationId: number, ecallId: number, body: TCreateEcallElevationRequest) {
		return axiosInstance.post<TElevationNumber>(`quotations/${quotationId}/ecall/${ecallId}/elevations/`, body)
	}

	async deleteEcallElevation(quotationId: number, ecallId: number, ecallElevationId: number) {
		return axiosInstance.delete<void>(`quotations/${quotationId}/ecall/${ecallId}/elevations/${ecallElevationId}/`)
	}

	async updateEcallElevations(quotationId: number, ecallId: number, body: TUpdateEcallElevationsRequest) {
		return axiosInstance.patch<TUpdateEcallElevationsResponse>(
			`quotations/${quotationId}/ecall/${ecallId}/elevations/`,
			body,
		)
	}

	async autoConnectEcallElevation(
		quotationId: number,
		ecallId: number,
		ecallElevationId: number,
		body: TAutoConnectEcallElevationRequest,
	) {
		return axiosInstance.post<TAutoConnectEcallElevationResponse>(
			`quotations/${quotationId}/ecall/${ecallId}/elevations/${ecallElevationId}/auto-connect/`,
			body,
		)
	}

	async updateErefConfiguration(quotationId: number, erefId: number, body: TUpdateErefConfigurationRequest) {
		return axiosInstance.patch(`quotations/${quotationId}/eref/${erefId}/configuration/`, body)
	}

	async autoConnectErefEcalls(quotationId: number, erefId: number, body: TAutoConnectErefEcallsRequest) {
		return axiosInstance.post<TAutoConnectErefEcallsResponse>(
			`quotations/${quotationId}/eref/${erefId}/auto-connect/`,
			body,
		)
	}

	async updateFurnitureCategory(quotationId: number, furnitureId: number, body: TUpdateFurnitureCategoryRequest) {
		return axiosInstance.patch(`quotations/${quotationId}/furniture/${furnitureId}/related-category/`, body)
	}

	async updateFurnitureFixedPrice(quotationId: number, furnitureId: number, body: TUpdateFurnitureFixedPriceRequest) {
		return axiosInstance.patch(`quotations/${quotationId}/furniture/${furnitureId}/fixed-price/`, body)
	}

	async updateFurniture(quotationId: number, furnitureId: number, body: TUpdateFurnitureRequest) {
		return axiosInstance.patch(`quotations/${quotationId}/furniture/${furnitureId}/`, body)
	}

	async updateFurnitureConfig(quotationId: number, furnitureId: number, body: TUpdateFurnitureConfigRequest) {
		return axiosInstance.patch(`quotations/${quotationId}/furniture/${furnitureId}/configuration/`, body)
	}

	async updateFurnitureComment(quotationId: number, furnitureId: number, body: TUpdateFurnitureCommentRequest) {
		return axiosInstance.put(`quotations/${quotationId}/furniture/${furnitureId}/comment/`, body)
	}

	async deleteFurnitureComment(quotationId: number, furnitureId: number, version: number) {
		return axiosInstance.delete(`quotations/${quotationId}/furniture/${furnitureId}/comment/`, {
			params: { version },
		})
	}

	async updateFurnitureStatus(quotationId: number, furnitureId: number, body: TUpdateFurnitureStatusRequest) {
		return axiosInstance.patch(`quotations/${quotationId}/furniture/${furnitureId}/status/`, body)
	}

	async duplicateFurniture(quotationId: number, furnitureId: number, body: TDuplicateFurnitureRequest) {
		return axiosInstance.post<TDuplicateFurnitureResponse>(
			`quotations/${quotationId}/furniture/${furnitureId}/duplicate/`,
			body,
		)
	}
}

export default new QuotationsService()
